import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Book } from '../types';

export interface CartItem {
  book: Book;
  quantity: number;
  dueDate?: string; // Ngày trả sách (ISO string), mặc định 10 ngày từ hôm nay
}

interface CartState {
  items: CartItem[];
  addItem: (book: Book) => void;
  removeItem: (bookId: string) => void;
  removeItems: (bookIds: string[]) => void;
  clearCart: () => void;
  updateQuantity: (bookId: string, quantity: number) => void;
  updateDueDate: (bookId: string, dueDate: string) => void;
  updateAllDueDates: (dueDate: string) => void; // Cập nhật ngày trả cho tất cả sách
  syncBookData: (book: Book) => void; // Đồng bộ dữ liệu sách từ server (để cập nhật availableCopies)
  getTotalItems: () => number;
  getTotalBooks: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
  
  addItem: (book) => {
    const items = get().items;
    const existingItem = items.find(item => item.book.id === book.id);
    
    if (existingItem) {
      // If book already in cart and available, increase quantity
      if (existingItem.quantity < book.availableCopies) {
        set({
          items: items.map(item =>
            item.book.id === book.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        });
      }
    } else {
      // Add new item to cart
      if (book.availableCopies > 0) {
        // Set default due date: 10 days from today
        const defaultDueDate = new Date();
        defaultDueDate.setDate(defaultDueDate.getDate() + 10);
        defaultDueDate.setHours(23, 59, 59, 999); // End of day
        
        set({
          items: [...items, { 
            book, 
            quantity: 1,
            dueDate: defaultDueDate.toISOString()
          }]
        });
      }
    }
  },
  
  removeItem: (bookId) => {
    set({
      items: get().items.filter(item => item.book.id !== bookId)
    });
  },
  
  removeItems: (bookIds) => {
    set({
      items: get().items.filter(item => !bookIds.includes(item.book.id))
    });
  },
  
  clearCart: () => {
    set({ items: [] });
  },
  
  updateQuantity: (bookId, quantity) => {
    const items = get().items;
    const item = items.find(item => item.book.id === bookId);
    
    if (!item) return;
    
    // Don't allow quantity to exceed available copies or go below 1
    const maxQuantity = item.book.availableCopies;
    const newQuantity = Math.max(1, Math.min(quantity, maxQuantity));
    
    set({
      items: items.map(item =>
        item.book.id === bookId
          ? { ...item, quantity: newQuantity }
          : item
      )
    });
  },

  updateDueDate: (bookId, dueDate) => {
    const items = get().items;
    set({
      items: items.map(item =>
        item.book.id === bookId
          ? { ...item, dueDate }
          : item
      )
    });
  },

  updateAllDueDates: (dueDate) => {
    const items = get().items;
    set({
      items: items.map(item => ({ ...item, dueDate }))
    });
  },

  syncBookData: (updatedBook) => {
    const items = get().items;
    const itemIndex = items.findIndex(item => item.book.id === updatedBook.id);
    
    if (itemIndex !== -1) {
      // Cập nhật book data nhưng giữ nguyên quantity và dueDate
      set({
        items: items.map(item =>
          item.book.id === updatedBook.id
            ? { ...item, book: updatedBook }
            : item
        )
      });
    }
  },
  
  getTotalItems: () => {
    return get().items.reduce((total, item) => total + item.quantity, 0);
  },
  
  getTotalBooks: () => {
    return get().items.length;
  }
    }),
    {
      name: 'cart-storage', // Tên key trong localStorage
      partialize: (state) => ({ items: state.items }) // Chỉ lưu items
    }
  )
);
